import { apiRequest } from "../../../../shared/lib/api";

export type NotificationItem = {
  id: number;
  type: string;
  title: string;
  description: string;
  icon: string;
  actionUrl: string | null;
  metadata?: {
    actionLabelKey?: "payNow" | "viewRegistration" | "viewGames" | string;
    relatedEntityId?: number | string | null;
    relatedEntityType?: string | null;
    status?: string | null;
    targetUrl?: string | null;
    [key: string]: unknown;
  };
  readAt: string | null;
  createdAt: string;
  status: "read" | "unread";
};

export type NotificationPreferences = {
  inApp: boolean;
  email: boolean;
  events: boolean;
  ranking: boolean;
  payments: boolean;
  achievements: boolean;
};

const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  inApp: true,
  email: true,
  events: true,
  ranking: true,
  payments: true,
  achievements: true,
};

export type NotificationsResponse = {
  unreadCount: number;
  notifications: NotificationItem[];
};

function authHeaders(token: string) {
  return {
    Authorization: `Bearer ${token}`,
  };
}

export function listMyNotifications(token: string) {
  return apiRequest<NotificationsResponse>("/api/me/notifications", {
    headers: authHeaders(token),
  });
}

export function markNotificationRead(token: string, id: number) {
  return apiRequest<NotificationsResponse>(`/api/me/notifications/${id}/read`, {
    method: "PUT",
    headers: authHeaders(token),
  });
}

export function markAllNotificationsRead(token: string) {
  return apiRequest<NotificationsResponse>("/api/me/notifications/read-all", {
    method: "PUT",
    headers: authHeaders(token),
  });
}

export function deleteNotification(token: string, id: number) {
  return apiRequest<NotificationsResponse>(`/api/me/notifications/${id}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
}

export function deleteAllNotifications(token: string) {
  return apiRequest<NotificationsResponse>("/api/me/notifications", {
    method: "DELETE",
    headers: authHeaders(token),
  });
}

type NotificationPreferencesResponse =
  | Partial<NotificationPreferences>
  | { preferences?: Partial<NotificationPreferences> };

function isNotificationPreferencesEnvelope(
  value: NotificationPreferencesResponse,
): value is { preferences?: Partial<NotificationPreferences> } {
  return Object.prototype.hasOwnProperty.call(value || {}, "preferences");
}

function normalizeNotificationPreferences(
  value: NotificationPreferencesResponse,
): NotificationPreferences {
  const raw: Partial<NotificationPreferences> =
    isNotificationPreferencesEnvelope(value)
      ? value.preferences || {}
      : value;
  return {
    inApp:
      typeof raw?.inApp === "boolean"
        ? raw.inApp
        : DEFAULT_NOTIFICATION_PREFERENCES.inApp,
    email:
      typeof raw?.email === "boolean"
        ? raw.email
        : DEFAULT_NOTIFICATION_PREFERENCES.email,
    events:
      typeof raw?.events === "boolean"
        ? raw.events
        : DEFAULT_NOTIFICATION_PREFERENCES.events,
    ranking:
      typeof raw?.ranking === "boolean"
        ? raw.ranking
        : DEFAULT_NOTIFICATION_PREFERENCES.ranking,
    payments:
      typeof raw?.payments === "boolean"
        ? raw.payments
        : DEFAULT_NOTIFICATION_PREFERENCES.payments,
    achievements:
      typeof raw?.achievements === "boolean"
        ? raw.achievements
        : DEFAULT_NOTIFICATION_PREFERENCES.achievements,
  };
}

export function getNotificationPreferences(token: string) {
  return apiRequest<NotificationPreferencesResponse>("/api/me/notification-preferences", {
    headers: authHeaders(token),
  }).then(normalizeNotificationPreferences);
}

export function saveNotificationPreferences(
  token: string,
  preferences: NotificationPreferences,
) {
  return apiRequest<NotificationPreferencesResponse>("/api/me/notification-preferences", {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify(preferences),
  }).then(normalizeNotificationPreferences);
}
